import { useEffect, useState } from 'react'
import { Separator } from '#app/shared/ui/data-display/Separator.js'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'

type Props = {}

type HeroFooterItem = {
  text: string
  image?: string
  isGoogleReviews?: boolean
}

const items = [
  {
    text: 'GARANTÍA INCLUÍDA',
    image: '/img/shield-tick.svg',
  },
  {
    text: 'valoración en Google',
    isGoogleReviews: true,
  },
  {
    text: 'FINANCIACIÓN A TU MEDIDA',
    image: '/img/shield-euro.svg',
  },
] satisfies HeroFooterItem[]

function GoogleRating({ text }: { text?: string }) {
  return (
    <a
      href="https://share.google/MqAddkG1otvG3L4wX"
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center justify-center text-white`}
    >
      <span className={`mr-1 text-lg font-bold`}>4.9</span>
      <span className={`mr-1 text-lg font-bold text-primary-500`}>★</span>
      <span className="text-base font-normal">
        {text ?? 'valoración en Google'}
      </span>
    </a>
  )
}

function BenefitItem({ item, text }: { item: HeroFooterItem; text?: string }) {
  if (item.isGoogleReviews) {
    return <GoogleRating text={text} />
  }

  return (
    <>
      <img src={item.image} className="h-5 w-5" alt={item.text} />
      <p className="text-subtitle2-semibold text-white">{text ?? item.text}</p>
    </>
  )
}

export default function HeroFooter({}: Props) {
  const [currentIndex, setCurrentTextIndex] = useState(0)
  const [displayedText, setDisplayedText] = useState('')

  useEffect(() => {
    let isCancelled = false
    const currentFullText = items[currentIndex]?.text
    let i = 0

    function typeLetter() {
      if (isCancelled || !currentFullText) return
      if (i < currentFullText.length) {
        setDisplayedText(currentFullText.slice(0, i + 1))
        i++
        setTimeout(typeLetter, 100)
      } else {
        setTimeout(deleteLetter, 2000)
      }
    }

    function deleteLetter() {
      if (isCancelled || !currentFullText) return
      if (i > 0) {
        setDisplayedText(currentFullText.slice(0, i - 1))
        i--
        setTimeout(deleteLetter, 100)
      } else {
        setCurrentTextIndex((prev) => (prev + 1) % items.length)
      }
    }

    typeLetter()

    return () => {
      isCancelled = true
    }
  }, [currentIndex])

  return (
    <div
      className={`
        h-[49px] w-full bg-primary-800

        lg:h-[60px]
      `}
    >
      <MainContainer
        className={`
          h-full px-4

          lg:px-5
        `}
      >
        {/* Mobile */}
        <div
          className={`
            relative flex h-full w-full items-center justify-center

            lg:hidden
          `}
        >
          <Separator
            className={`
              absolute left-0 right-0 top-0 !rounded-lg bg-line-gradient-light
              opacity-[37%]
            `}
          />
          <div
            className={`
              flex w-full items-center justify-center gap-1.5 text-center
            `}
          >
            <BenefitItem item={items[currentIndex]!} text={displayedText} />
          </div>
        </div>
        {/* Desktop */}
        <div
          className={`
            hidden h-full w-full items-center

            lg:flex
          `}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className="flex h-full flex-1 items-center gap-1.5"
            >
              <div
                className={`
                  mx-auto flex w-full items-center justify-center gap-1.5
                  text-center
                `}
              >
                <BenefitItem item={item} />
              </div>
              {index < items.length - 1 && (
                <Separator
                  orientation="vertical"
                  className={`
                    hidden h-full self-end bg-line-gradient-light opacity-[37%]

                    lg:inline-flex
                  `}
                />
              )}
            </div>
          ))}
        </div>
      </MainContainer>
    </div>
  )
}
